import { detect } from 'jschardet';

interface DetectionResult {
  success: boolean;
  encoding: string;
  confidence: number;
  text: string;
  error?: string;
}

const COMMON_ENCODINGS = [
  'UTF-8',
  'Windows-1252', // Excel default
  'ISO-8859-1', // Legacy Latin-1
];

const HIGH_CONFIDENCE_THRESHOLD = 0.8;

export const detectFileEncoding = (buffer: ArrayBuffer): DetectionResult => {
  try {
    // Handle empty buffer
    if (buffer.byteLength === 0) {
      return {
        success: false,
        encoding: 'UTF-8',
        confidence: 0,
        text: '',
        error: 'Empty buffer',
      };
    }

    // Convert ArrayBuffer to Buffer for jschardet
    const uint8Array = new Uint8Array(buffer);
    const bufferData = Buffer.from(uint8Array);
    const detection = detect(bufferData);

    if (detection && detection.confidence > HIGH_CONFIDENCE_THRESHOLD) {
      // High confidence detection - use it directly
      try {
        // Map ascii to UTF-8 since ASCII is a subset of UTF-8
        const detectedEncoding =
          detection.encoding === 'ascii' ? 'UTF-8' : detection.encoding;
        const decoder = new TextDecoder(detectedEncoding);
        const text = decoder.decode(buffer);

        if (validateFrenchText(text)) {
          return {
            success: true,
            encoding: detectedEncoding,
            confidence: detection.confidence,
            text,
          };
        }
      } catch {
        // Detection failed, fall back to common encodings
      }
    }
    // Low confidence or validation failed - try common encodings
    return tryCommonEncodings(buffer);
  } catch {
    // jschardet failed - try common encodings
    return tryCommonEncodings(buffer);
  }
};

const tryCommonEncodings = (buffer: ArrayBuffer): DetectionResult => {
  for (const encoding of COMMON_ENCODINGS) {
    try {
      const decoder = new TextDecoder(encoding);
      const text = decoder.decode(buffer);

      if (validateFrenchText(text)) {
        return {
          success: true,
          encoding,
          confidence: 0.5, // Manual fallback confidence
          text,
        };
      }
    } catch {
      continue; // Try next encoding
    }
  }

  // If all encodings failed, return UTF-8 as last resort
  try {
    const decoder = new TextDecoder('UTF-8');
    const text = decoder.decode(buffer);

    return {
      success: false,
      encoding: 'UTF-8',
      confidence: 0,
      text,
      error: 'No valid encoding found',
    };
  } catch {
    return {
      success: false,
      encoding: 'UTF-8',
      confidence: 0,
      text: '',
      error: 'Failed to decode file',
    };
  }
};

export const validateFrenchText = (text: string): boolean => {
  // Check for replacement characters (encoding failure indicators)
  if (text.includes('\uFFFD')) {
    return false;
  }

  // Check for common encoding failure patterns
  // These patterns indicate Windows-1252 content read as UTF-8
  const encodingFailurePatterns = [
    'Ã©',
    'Ã¨',
    'Ã§',
    'Ã ',
    'Ã¢',
    'Ã´',
    'Ã¹',
    'Ã»',
    'Ã®',
    'Ã¯',
    'Ã«',
    'Ã¼',
    'Ã¿',
  ];

  for (const pattern of encodingFailurePatterns) {
    if (text.includes(pattern)) {
      return false;
    }
  }

  // If we reach here, no obvious encoding failures were detected
  return true;
};

export const getSupportedEncodings = (): string[] => {
  return [...COMMON_ENCODINGS];
};
